import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type {
  CreateReportPayload,
  CreditReportDetail,
  CreditReportListItem,
  DiffResult,
} from './reports.types';

export function useReports(clientId: string | null | undefined) {
  return useQuery({
    enabled: Boolean(clientId),
    queryKey: ['reports', 'list', clientId],
    queryFn: async () => {
      const r = await api.get<CreditReportListItem[]>(
        `/clients/${clientId}/reports`,
      );
      return r.data;
    },
  });
}

export function useReport(reportId: string | null | undefined) {
  return useQuery({
    enabled: Boolean(reportId),
    queryKey: ['reports', 'detail', reportId],
    queryFn: async () => {
      const r = await api.get<CreditReportDetail>(`/reports/${reportId}`);
      return r.data;
    },
  });
}

export function useDiff(reportId: string | null | undefined) {
  return useQuery({
    enabled: Boolean(reportId),
    queryKey: ['reports', 'diff', reportId],
    queryFn: async () => {
      const r = await api.get<DiffResult>(`/reports/${reportId}/diff`);
      return r.data;
    },
  });
}

export function useCreateReport(clientId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateReportPayload) => {
      const r = await api.post(`/clients/${clientId}/reports`, payload);
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reports', 'list', clientId] });
    },
  });
}

export function useUploadPdfReport(clientId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post(`/clients/${clientId}/reports/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reports', 'list', clientId] });
    },
  });
}